import React, { useState, useEffect } from 'react';
import { X, Save, UserPlus } from 'lucide-react';
import { auth } from '../firebase';
import toast from 'react-hot-toast';
import Select from 'react-select';
import type { Client, SearchCriteria } from '../types';
import { cities, neighborhoods } from '../data';
import { triggerMatchDetection, buyerRelevantFieldsChanged } from '../utils/smartAlerts';
import MoneyInput from './MoneyInput';

function toLocalDatetimeString(utcString: string): string {
  const d = new Date(utcString);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface AddClientModalProps {
  client: Client | null;
  onClose: () => void;
  onSave: () => void;
}

const propertyTypes = [
  { value: 'apartment', label: 'דירת מגורים' },
  { value: 'duplex', label: 'דופלקס' },
  { value: 'penthouse', label: 'פנטהאוז' },
  { value: 'garden-apartment', label: 'דירת גן' },
  { value: 'house', label: 'בית פרטי' },
  { value: 'commercial', label: 'מסחרי' },
];

const roomOptions = [
  { value: '1', label: '1 חדר' },
  { value: '2', label: '2 חדרים' },
  { value: '3', label: '3 חדרים' },
  { value: '4', label: '4 חדרים' },
  { value: '5', label: '5 חדרים' },
  { value: '6', label: '6+ חדרים' },
];

const customerStatusOptions = [
  { value: 'new', label: 'חדש' },
  { value: 'in_progress', label: 'בטיפול' },
  { value: 'available', label: 'זמין' },
  { value: 'unavailable', label: 'לא זמין' },
  { value: 'serious', label: 'רציני' },
  { value: 'met', label: 'ניפגש' },
  { value: 'property_visit', label: 'ביקור בנכס' },
  { value: 'deal_closed', label: 'נסגרה עסקה' },
  { value: 'not_relevant', label: 'לא רלוונטי' },
];

const AddClientModal: React.FC<AddClientModalProps> = ({ client, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [leadType, setLeadType] = useState<'buyer' | 'seller'>('buyer');
  const [customerStatus, setCustomerStatus] = useState('new');
  const [lastContactAt, setLastContactAt] = useState('');
  const [nextFollowUpAt, setNextFollowUpAt] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
    propertyTypes: [],
    priceMin: undefined,
    priceMax: undefined,
    priceTolerance: 5,
    rooms: [],
    cities: [],
    neighborhoods: [],
    minSize: undefined,
    maxSize: undefined,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setName(client.name);
      setPhone(client.phone);
      setEmail(client.email || '');
      setNotes(client.notes || '');
      setLeadType(client.leadType || 'buyer');
      const rawStatus = client.customerStatus || 'new';
      setCustomerStatus(rawStatus === 'closed' ? 'deal_closed' : rawStatus);
      setLastContactAt(client.lastContactAt ? toLocalDatetimeString(client.lastContactAt) : '');
      setNextFollowUpAt(client.nextFollowUpAt ? toLocalDatetimeString(client.nextFollowUpAt) : '');
      setNextAction(client.nextAction || '');
      setSearchCriteria(client.searchCriteria);
    }
  }, [client]);

  const handleSave = async () => {
    if (!auth.currentUser) {
      toast.error('אין משתמש מחובר');
      return;
    }

    if (!name.trim()) {
      toast.error('נא להזין שם לקוח');
      return;
    }

    if (!phone.trim()) {
      toast.error('נא להזין מספר טלפון');
      return;
    }

    try {
      setSaving(true);

      const idToken = await auth.currentUser.getIdToken();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const cleanedSearchCriteria: any = {
        propertyTypes: searchCriteria.propertyTypes || [],
        rooms: searchCriteria.rooms || [],
        cities: searchCriteria.cities || [],
        neighborhoods: searchCriteria.neighborhoods || [],
      };

      if (searchCriteria.priceMin !== undefined && searchCriteria.priceMin !== null) {
        cleanedSearchCriteria.priceMin = searchCriteria.priceMin;
      }

      if (searchCriteria.priceMax !== undefined && searchCriteria.priceMax !== null) {
        cleanedSearchCriteria.priceMax = searchCriteria.priceMax;
      }

      if (searchCriteria.priceTolerance !== undefined && searchCriteria.priceTolerance !== null) {
        cleanedSearchCriteria.priceTolerance = searchCriteria.priceTolerance;
      }

      if (searchCriteria.minSize !== undefined && searchCriteria.minSize !== null) {
        cleanedSearchCriteria.minSize = searchCriteria.minSize;
      }

      if (searchCriteria.maxSize !== undefined && searchCriteria.maxSize !== null) {
        cleanedSearchCriteria.maxSize = searchCriteria.maxSize;
      }

      if (client) {
        const apiUrl = `${supabaseUrl}/functions/v1/update-lead`;
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            leadId: client.id,
            name: name.trim(),
            phone: phone.trim(),
            email: email.trim() || '',
            notes: notes.trim() || '',
            leadType: leadType,
            customerStatus: customerStatus,
            lastContactAt: lastContactAt ? new Date(lastContactAt).toISOString() : null,
            nextFollowUpAt: nextFollowUpAt ? new Date(nextFollowUpAt).toISOString() : null,
            nextAction: nextAction || null,
            searchCriteria: cleanedSearchCriteria,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update lead');
        }

        toast.success('הלקוח עודכן בהצלחה');

        // Smart Alerts: trigger match detection if buyer-relevant fields changed
        if (leadType !== 'seller') {
          const beforeCriteria = (client as any).searchCriteria || {};
          const beforeFlat = {
            searchCriteria: beforeCriteria,
          };
          const afterFlat = {
            searchCriteria: cleanedSearchCriteria,
          };
          if (buyerRelevantFieldsChanged(beforeFlat, afterFlat)) {
            triggerMatchDetection('buyer_updated', client.id, 'buyer');
            setTimeout(() => window.dispatchEvent(new Event('smart-alerts:refresh')), 3000);
          }
        }
      } else {
        const apiUrl = `${supabaseUrl}/functions/v1/add-lead`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: name.trim(),
            phone: phone.trim(),
            email: email.trim() || '',
            notes: notes.trim() || '',
            source: 'manual',
            stage: 'lead',
            leadType: leadType,
            customerStatus: customerStatus,
            nextAction: nextAction || null,
            searchCriteria: cleanedSearchCriteria,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create lead');
        }

        const responseData = await response.json();
        toast.success('הלקוח נוסף בהצלחה');

        // Smart Alerts: trigger match detection for new buyer
        if (leadType !== 'seller' && responseData.lead?.id) {
          triggerMatchDetection('buyer_new', String(responseData.lead.id), 'buyer');
          setTimeout(() => window.dispatchEvent(new Event('smart-alerts:refresh')), 3000);
        }
      }

      onSave();
    } catch (error: any) {
      console.error('Error saving client:', error);
      toast.error(error.message || 'שגיאה בשמירת הלקוח');
    } finally {
      setSaving(false);
    }
  };

  const selectedPropertyTypes = propertyTypes.filter(pt =>
    searchCriteria.propertyTypes?.includes(pt.value)
  );

  const selectedRooms = roomOptions.filter(r =>
    searchCriteria.rooms?.includes(Number(r.value))
  );

  const selectedCities = cities.filter(c =>
    searchCriteria.cities?.includes(c.value)
  );

  const availableNeighborhoods = selectedCities.flatMap(city =>
    neighborhoods[city.value] || []
  );

  const selectedNeighborhoods = availableNeighborhoods.filter(n =>
    searchCriteria.neighborhoods?.includes(n.value)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center gap-3">
            <UserPlus size={28} className="text-white" />
            <h2 className="text-2xl font-bold text-white">
              {client ? 'עריכת לקוח' : 'לקוח חדש'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">פרטי לקוח</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    שם מלא <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    placeholder="הזן שם מלא"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    טלפון <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    placeholder="050-1234567"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    אימייל
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    placeholder="example@email.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    סוג לקוח <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-3 flex-1 px-4 py-3 rounded-lg bg-gray-700 border-2 cursor-pointer transition-all hover:border-blue-500"
                      style={{ borderColor: leadType === 'buyer' ? '#3B82F6' : '#4B5563' }}
                    >
                      <input
                        type="radio"
                        name="leadType"
                        value="buyer"
                        checked={leadType === 'buyer'}
                        onChange={(e) => setLeadType(e.target.value as 'buyer' | 'seller')}
                        className="w-5 h-5 accent-blue-500"
                      />
                      <div>
                        <span className="text-white font-medium">קונה נכס</span>
                        <p className="text-sm text-gray-400">לקוח המחפש נכס לרכישה</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 flex-1 px-4 py-3 rounded-lg bg-gray-700 border-2 cursor-pointer transition-all hover:border-orange-500"
                      style={{ borderColor: leadType === 'seller' ? '#EA580C' : '#4B5563' }}
                    >
                      <input
                        type="radio"
                        name="leadType"
                        value="seller"
                        checked={leadType === 'seller'}
                        onChange={(e) => setLeadType(e.target.value as 'buyer' | 'seller')}
                        className="w-5 h-5 accent-orange-500"
                      />
                      <div>
                        <span className="text-white font-medium">מוכר נכס</span>
                        <p className="text-sm text-gray-400">לקוח המעוניין למכור נכס</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    סטטוס לקוח
                  </label>
                  <select
                    value={customerStatus}
                    onChange={(e) => setCustomerStatus(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  >
                    {customerStatusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">קריטריוני חיפוש</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    סוגי נכס
                  </label>
                  <Select
                    isMulti
                    options={propertyTypes}
                    value={selectedPropertyTypes}
                    onChange={(selected) =>
                      setSearchCriteria(prev => ({
                        ...prev,
                        propertyTypes: selected.map(s => s.value)
                      }))
                    }
                    placeholder="בחר סוגי נכס"
                    className="select-container"
                    classNamePrefix="select"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      מחיר מינימום
                    </label>
                    <MoneyInput
                      value={searchCriteria.priceMin}
                      onChange={(v) =>
                        setSearchCriteria(prev => ({
                          ...prev,
                          priceMin: v
                        }))
                      }
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      placeholder="1,000,000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      מחיר מקסימום
                    </label>
                    <MoneyInput
                      value={searchCriteria.priceMax}
                      onChange={(v) =>
                        setSearchCriteria(prev => ({
                          ...prev,
                          priceMax: v
                        }))
                      }
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      placeholder="2,000,000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    טולרנס מחיר: {searchCriteria.priceTolerance || 5}%
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="7"
                      value={searchCriteria.priceTolerance || 5}
                      onChange={(e) =>
                        setSearchCriteria(prev => ({
                          ...prev,
                          priceTolerance: Number(e.target.value)
                        }))
                      }
                      className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                    <div className="flex gap-2 text-xs text-gray-400">
                      <span>1%</span>
                      <span>-</span>
                      <span>7%</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    הטולרנס מגדיר את אחוז הגמישות בטווח המחירים
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    מספר חדרים
                  </label>
                  <Select
                    isMulti
                    options={roomOptions}
                    value={selectedRooms}
                    onChange={(selected) =>
                      setSearchCriteria(prev => ({
                        ...prev,
                        rooms: selected.map(s => Number(s.value))
                      }))
                    }
                    placeholder="בחר מספר חדרים"
                    className="select-container"
                    classNamePrefix="select"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    ערים
                  </label>
                  <Select
                    isMulti
                    options={cities}
                    value={selectedCities}
                    onChange={(selected) => {
                      const newCities = selected.map(s => s.value);
                      setSearchCriteria(prev => {
                        const validNeighborhoods = prev.neighborhoods?.filter(nh => {
                          return newCities.some(cityValue =>
                            neighborhoods[cityValue]?.some(n => n.value === nh)
                          );
                        }) || [];

                        return {
                          ...prev,
                          cities: newCities,
                          neighborhoods: validNeighborhoods
                        };
                      });
                    }}
                    placeholder="בחר ערים"
                    className="select-container"
                    classNamePrefix="select"
                  />
                </div>

                {selectedCities.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      שכונות
                    </label>
                    <Select
                      isMulti
                      options={availableNeighborhoods}
                      value={selectedNeighborhoods}
                      onChange={(selected) =>
                        setSearchCriteria(prev => ({
                          ...prev,
                          neighborhoods: selected.map(s => s.value)
                        }))
                      }
                      placeholder={availableNeighborhoods.length > 0 ? "בחר שכונות" : "אין שכונות זמינות לערים שנבחרו"}
                      isDisabled={availableNeighborhoods.length === 0}
                      className="select-container"
                      classNamePrefix="select"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      גודל מינימום (מ"ר)
                    </label>
                    <input
                      type="number"
                      value={searchCriteria.minSize || ''}
                      onChange={(e) =>
                        setSearchCriteria(prev => ({
                          ...prev,
                          minSize: e.target.value ? Number(e.target.value) : undefined
                        }))
                      }
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      placeholder="50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      גודל מקסימום (מ"ר)
                    </label>
                    <input
                      type="number"
                      value={searchCriteria.maxSize || ''}
                      onChange={(e) =>
                        setSearchCriteria(prev => ({
                          ...prev,
                          maxSize: e.target.value ? Number(e.target.value) : undefined
                        }))
                      }
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      placeholder="150"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">מעקב ותזכורות</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    פעולה הבאה
                  </label>
                  <select
                    value={nextAction}
                    onChange={(e) => setNextAction(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">ללא</option>
                    <option value="להתקשר">להתקשר</option>
                    <option value="לשלוח WhatsApp">לשלוח WhatsApp</option>
                    <option value="לשלוח נכס">לשלוח נכס</option>
                    <option value="לתאם ביקור">לתאם ביקור</option>
                    <option value="לבדוק התאמה">לבדוק התאמה</option>
                    <option value="להמתין לתשובה">להמתין לתשובה</option>
                    <option value="אחר">אחר</option>
                  </select>
                </div>
                {client && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      קשר אחרון
                    </label>
                    <input
                      type="datetime-local"
                      value={lastContactAt}
                      onChange={(e) => setLastContactAt(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    תזכורת למעקב הבא
                  </label>
                  <input
                    type="datetime-local"
                    value={nextFollowUpAt}
                    onChange={(e) => setNextFollowUpAt(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                הערות
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 h-24"
                placeholder="הערות נוספות על הלקוח..."
              />
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-700 bg-gray-750 flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
          >
            ביטול
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            {saving ? 'שומר...' : 'שמור'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddClientModal;
